import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, AlertCircle, Loader2, Mail, User, MessageSquare, Phone, MapPin } from 'lucide-react';
import Button from '../ui/Button';
import API_URL from '../../config';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setStatus('loading');
    setErrorMsg('');
    
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message
        }),
      });

      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setErrorMsg(errorData.error || 'Failed to send message. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Contact Form Error:', err);
      setErrorMsg('Network error. Please verify your connection or email us directly.');
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 relative bg-slate-50/60 dark:bg-slate-900/40 border-t border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              Connect With Us
            </div>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              Get in Touch
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto text-lg leading-relaxed">
              Have a project inquiry, product partnership, or technical question? Send us a message and our team will get back to you promptly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            
            {/* Direct Contact Details */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-subtle space-y-6">
                <div>
                  <h3 className="font-display font-bold text-slate-900 dark:text-white text-base mb-1">
                    Corporate Office
                  </h3>
                  <p className="text-xs text-slate-500">Registered in India</p>
                </div>

                <div className="space-y-4 text-sm text-slate-600 dark:text-slate-300">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-brand-600 w-5 h-5 shrink-0 mt-0.5" />
                    <span className="leading-relaxed text-xs">
                      Babhnauli, Sant Kabir Nagar, Uttar Pradesh 272125, India
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="text-brand-600 w-4 h-4 shrink-0" />
                    <span className="text-xs font-medium">+91 7052608972</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="text-brand-600 w-4 h-4 shrink-0" />
                    <span className="text-xs font-medium">contact@mirafuturetech.com</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500">
                  <p className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Response Guarantee</p>
                  <p>Inquiries reviewed by our engineering and leadership team within 24-48 business hours.</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-8">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-10 shadow-card">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                        Your Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                        <input 
                          id="name"
                          required
                          type="text" 
                          value={form.name}
                          onChange={handleChange}
                          className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                        <input 
                          id="email"
                          required
                          type="email" 
                          value={form.email}
                          onChange={handleChange}
                          className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                      Your Message *
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                      <textarea 
                        id="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all resize-none"
                        placeholder="Tell us about your project, timeline, or inquiry..."
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                    <AnimatePresence mode="wait">
                      {status === 'success' && (
                        <motion.div 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center gap-2 text-emerald-600 text-xs font-semibold"
                        >
                          <CheckCircle2 size={16} /> Thank you! Your message has been sent successfully.
                        </motion.div>
                      )}
                      {status === 'error' && (
                        <motion.div 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center gap-2 text-red-600 text-xs font-semibold"
                        >
                          <AlertCircle size={16} /> {errorMsg}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="ml-auto w-full sm:w-auto">
                      <Button 
                        type="submit"
                        disabled={status === 'loading'}
                        variant="primary" 
                        className="w-full sm:w-auto px-7 py-3 font-semibold shadow-xs flex items-center justify-center gap-2 min-w-[170px]"
                      >
                        {status === 'loading' ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message <Send size={15} />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
